import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { CheckCircle2 } from 'lucide-react';

export interface VaccineType {
  id: string;
  emoji: string;
  days: number[];
  color: string;
}

interface VaccineTypePickerProps {
  vaccines: VaccineType[];
  currentDay: number;
  selectedVaccine: string | null;
  completedToday: string[];
  onSelect: (vaccineId: string) => void;
}

export const VaccineTypePicker: React.FC<VaccineTypePickerProps> = ({
  vaccines,
  currentDay,
  selectedVaccine,
  completedToday,
  onSelect,
}) => {
  const { t } = useTranslation();

  return (
    <Card className="p-4">
      <h3 className="text-lg font-medium mb-4 text-center">
        {t('cbs.vaccination.selectVaccine')}
      </h3>
      <div className="grid grid-cols-2 gap-3">
        {vaccines.map((vaccine) => {
          const isRecommended = vaccine.days.includes(currentDay);
          const isDone = completedToday.includes(vaccine.id);

          return (
            <button
              key={vaccine.id}
              onClick={() => !isDone && onSelect(vaccine.id)}
              disabled={isDone}
              className={cn(
                'flex flex-col items-center p-5 rounded-xl border-2 transition-all relative',
                isDone
                  ? 'border-success bg-success/10 opacity-60'
                  : selectedVaccine === vaccine.id
                    ? 'border-primary bg-primary/10 scale-105'
                    : 'border-border hover:border-primary/50',
                isRecommended && !isDone && 'ring-2 ring-warning ring-offset-2'
              )}
            >
              {isDone && (
                <CheckCircle2 className="absolute top-2 right-2 w-6 h-6 text-success" />
              )}
              <span className="text-5xl mb-2">{vaccine.emoji}</span>
              <span className="text-sm font-bold text-center">
                {t(`cbs.vaccination.vaccines.${vaccine.id}`)}
              </span>
              <span className="text-xs text-muted-foreground mt-1">
                {t('cbs.batch.day')} {vaccine.days.join(', ')}
              </span>
              {isRecommended && !isDone && (
                <span className="mt-2 text-xs bg-warning text-warning-foreground px-2 py-0.5 rounded-full">
                  {t('cbs.vaccination.today')}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </Card>
  );
};

export default VaccineTypePicker;
